import { PROOF_LIBRARY_VIEW_PARAMS } from '@/lib/proof-library/library-view';

const PROOF_ROOT = '/customer-ops/proof';
const LOCAL_ORIGIN = 'https://admin.invalid';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const FAMILY_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,119}$/;
const ALLOWED_PARAMS = new Set<string>([...PROOF_LIBRARY_VIEW_PARAMS, 'proof', 'proofId', 'run', 'add']);

function hasControlCharacters(value: string): boolean {
  return [...value].some((character) => {
    const codePoint = character.charCodeAt(0);
    return codePoint <= 31 || codePoint === 127;
  });
}

/**
 * A `?returnTo=` value is safe only if it points back at the proof library page itself with recognized
 * query keys and no control characters — the page's own readers already default safely on anything else
 * (an unrecognized filter value just falls back), so this only has to rule out an open redirect or a
 * header-injection payload riding along in the URL.
 */
export function safeProofReturnTo(value: string | null | undefined): string | null {
  if (!value || value.length > 500 || !value.startsWith('/') || value.startsWith('//')) return null;
  if (value.includes('\\') || hasControlCharacters(value)) return null;
  let url: URL;
  try {
    url = new URL(value, LOCAL_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== LOCAL_ORIGIN || url.hash || url.username || url.password) return null;
  if (url.pathname !== PROOF_ROOT) return null;
  const params = url.searchParams;
  const keys = [...params.keys()];
  if (!keys.every((key) => ALLOWED_PARAMS.has(key) && params.getAll(key).length === 1)) return null;
  const proof = params.get('proof') ?? params.get('proofId');
  if (proof !== null && !(UUID.test(proof) || FAMILY_ID.test(proof))) return null;
  const run = params.get('run');
  if (run !== null && !UUID.test(run)) return null;
  for (const key of ['q', 'saved', 'location', 'archived', 'add'] as const) {
    const filterValue = params.get(key);
    if (filterValue !== null && (filterValue.length > 120 || hasControlCharacters(filterValue))) return null;
  }
  const query = params.toString();
  return `${PROOF_ROOT}${query ? `?${query}` : ''}`;
}

export function proofLoginHref(returnTo: string): string {
  const safe = safeProofReturnTo(returnTo);
  return safe ? `/login?returnTo=${encodeURIComponent(safe)}` : '/login';
}
